import React, { ReactNode } from 'react';
import { Box, BoxProps, Button, ButtonProps, Flex, Text } from '@chakra-ui/react';
import { ArrowLeft, Check, X } from 'phosphor-react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { FramedCard, FramedCardContent, FramedCardHeader } from './FramedCard';

interface FormBreadcrumbProps {
  backTo: string;
  backLabel: string;
  currentLabel: string;
}

export const FormBreadcrumb = ({ backTo, backLabel, currentLabel }: FormBreadcrumbProps) => (
  <Box as="nav" aria-label="Navegação estrutural" minW={0}>
    <Flex as="ol" role="list" align="center" gap="12px" listStyleType="none" m={0} p={0} minW={0}>
      <Box as="li" flexShrink={0}>
        <Text
          as={RouterLink}
          to={backTo}
          display="inline-flex"
          alignItems="center"
          gap="6px"
          textDecoration="underline"
          textUnderlineOffset="3px"
          fontSize="13px"
          lineHeight="20px"
          fontWeight={500}
          color="content.body"
          borderRadius="sm"
          _hover={{ color: 'content.strong' }}
          _focusVisible={{ boxShadow: 'outline', outline: 'none' }}
        >
          <ArrowLeft size={14} aria-hidden="true" focusable="false" />
          {backLabel}
        </Text>
      </Box>
      <Box as="li" aria-hidden flexShrink={0} fontSize="13px" color="content.body">
        /
      </Box>
      <Box
        as="li"
        aria-current="page"
        minW={0}
        fontSize="13px"
        lineHeight="20px"
        fontWeight={500}
        color="content.strong"
        isTruncated
      >
        {currentLabel}
      </Box>
    </Flex>
  </Box>
);

interface FormShellProps extends Omit<BoxProps, 'title'> {
  title: string;
  description?: string;
  backTo?: string;
  backLabel?: string;
  children: ReactNode;
}

export const FormShell = ({
  title,
  description,
  backTo,
  backLabel,
  children,
  ...rest
}: FormShellProps) => (
  <Box display="flex" flexDirection="column" gap="20px" w="100%" minW={0} {...rest}>
    <Box as="header" display="flex" flexDirection="column" gap="12px" minW={0}>
      {backTo && backLabel && (
        <FormBreadcrumb backTo={backTo} backLabel={backLabel} currentLabel={title} />
      )}
      <Box display="flex" flexDirection="column" gap="4px" minW={0}>
        <Text
          as="h1"
          fontFamily="display"
          fontSize="24px"
          lineHeight="30px"
          fontWeight={500}
          color="content.strong"
        >
          {title}
        </Text>
        {description && (
          <Text fontSize="13px" lineHeight="20px" color="content.body" maxW="68ch">
            {description}
          </Text>
        )}
      </Box>
    </Box>
    {children}
  </Box>
);

interface FormSectionProps extends Omit<BoxProps, 'title'> {
  title: string;
  description?: string;
  children: ReactNode;
}

export const FormSection = ({ title, description, children, ...rest }: FormSectionProps) => (
  <FramedCard role="region" aria-label={title} {...rest}>
    <FramedCardHeader px="16px" py="8px">
      <Text as="h2" fontSize="13px" lineHeight="20px" fontWeight={500} color="content.strong">
        {title}
      </Text>
    </FramedCardHeader>
    <FramedCardContent p="16px" gap="16px" overflow="visible">
      {description && (
        <Text fontSize="13px" lineHeight="20px" color="content.body">
          {description}
        </Text>
      )}
      {children}
    </FramedCardContent>
  </FramedCard>
);

export const FormRow = ({ children, ...rest }: BoxProps) => (
  <Box
    display="grid"
    gridTemplateColumns={{ base: '1fr', md: 'repeat(auto-fit, minmax(220px, 1fr))' }}
    gap="16px"
    alignItems="start"
    {...rest}
  >
    {children}
  </Box>
);

interface FormActionsProps {
  cancelTo?: string;
  submitLabel?: string;
  cancelLabel?: string;
  isLoading?: boolean;
  isDisabled?: boolean;
  submitProps?: ButtonProps;
  children?: ReactNode;
}

export const FormActions = ({
  cancelTo,
  submitLabel = 'Salvar',
  cancelLabel = 'Cancelar',
  isLoading,
  isDisabled,
  submitProps,
  children,
}: FormActionsProps) => {
  const navigate = useNavigate();

  return (
    <Flex align="center" justify="flex-end" gap="8px" flexWrap="wrap" pt="4px">
      {children}
      {cancelTo && (
        <Button
          type="button"
          variant="outline"
          leftIcon={<X size={14} />}
          onClick={() => navigate(cancelTo)}
        >
          {cancelLabel}
        </Button>
      )}
      <Button
        type="submit"
        leftIcon={<Check size={14} />}
        isLoading={isLoading}
        isDisabled={isDisabled}
        {...submitProps}
      >
        {submitLabel}
      </Button>
    </Flex>
  );
};
